import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  AccessTime as ClockIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { Incident, IncidentListResponse } from '../Types/Incident';
import { getIncidents } from '../Services/IncidentService';
import IncidentRow from '../Components/Incidents/IncidentRow';
import { appColors } from '../Config/Theme';
import { formatTime } from '../Utils/dateHelpers';
import { isIncidentClosed, getIncidentOperationalState, sortByOperationalUrgency } from '../Utils/incidentHelpers';
import { useSecurity } from '../Contexts/SecurityProvider';
import { useNowTick } from '../Hooks/useNowTick';

const AUTO_REFRESH_MS = 15_000;
const DEFAULT_PAST_VISIBLE = 3;
const ALL_LOCATIONS_VALUE = '__all__';

function normalizeLocationName(value?: string | null): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

interface LocationOption {
  value: string;
  label: string;
}

function getLocationOptions(incidents: Incident[]): LocationOption[] {
  const seen = new Map<string, string>();
  for (const inc of incidents) {
    const raw = (inc.location ?? '').trim();
    if (!raw) continue;
    const norm = normalizeLocationName(raw);
    if (!seen.has(norm)) seen.set(norm, raw);
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ── Summary stat pill ──
interface SummaryItemProps {
  label: string;
  count: number;
  color?: string;
  bg?: string;
  border?: string;
  highlight?: boolean;
}

const SummaryItem: React.FC<SummaryItemProps> = ({ label, count, color, bg, border, highlight }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.5,
      borderRadius: '8px',
      bgcolor: bg ?? appColors.surface.card,
      border: `1px solid ${border ?? appColors.border.default}`,
    }}
  >
    <Typography variant="caption" sx={{ color: color ?? appColors.text.body, fontWeight: 600, fontSize: '0.75rem' }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: '0.875rem',
        color: highlight && count > 0 ? appColors.status.error : (color ?? appColors.text.heading),
      }}
    >
      {count}
    </Typography>
  </Box>
);

const IncidentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, hasAnyRole } = useSecurity();
  const [response, setResponse] = useState<IncidentListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showAllPast, setShowAllPast] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>(ALL_LOCATIONS_VALUE);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });

  const canCreateIncident = hasPermission('Incident.Create');
  const canEdit = hasAnyRole(['Admin', 'IncidentCommander']);

  const fetchData = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const data = await getIncidents();
      setResponse(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load incidents.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);

    timerRef.current = setInterval(() => fetchData(false), AUTO_REFRESH_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchData]);

  // Derive location options from all incidents
  const allIncidents = useMemo(() => response?.data ?? [], [response]);
  const locationOptions = useMemo(() => getLocationOptions(allIncidents), [allIncidents]);

  // Apply location filter
  const locationFiltered = useMemo(() => {
    if (selectedLocation === ALL_LOCATIONS_VALUE) return allIncidents;
    return allIncidents.filter(i => normalizeLocationName(i.location) === selectedLocation);
  }, [allIncidents, selectedLocation]);

  const { active, past } = useMemo(() => ({
    active: sortByOperationalUrgency(locationFiltered.filter((i: Incident) => !isIncidentClosed(i))),
    past: locationFiltered
      .filter((i: Incident) => isIncidentClosed(i))
      .sort((a, b) => new Date(b.closedAtUtc ?? b.startedAtUtc).getTime() - new Date(a.closedAtUtc ?? a.startedAtUtc).getTime()),
  }), [locationFiltered]);

  // Summary counts
  const summaryCounts = useMemo(() => {
    let needsAttention = 0;
    let notStarted = 0;
    let inProgress = 0;
    active.forEach((inc) => {
      const state = getIncidentOperationalState(inc);
      if (state === 'needsAttention') needsAttention++;
      else if (state === 'notStarted') notStarted++;
      else if (state === 'inProgress') inProgress++;
    });
    return { needsAttention, notStarted, inProgress };
  }, [active]);

  const lastUpdated = useMemo(() => {
    if (!response?.meta?.serverTimeUtc) return null;
    return formatTime(response.meta.serverTimeUtc);
  }, [response]);

  const visiblePast = showAllPast ? past : past.slice(0, DEFAULT_PAST_VISIBLE);
  const hasMorePast = past.length > DEFAULT_PAST_VISIBLE;

  // Shared once-per-second tick for all active incident elapsed timers
  const now = useNowTick(1000, response?.meta?.serverTimeUtc);

  return (
    <Box sx={{
      pt: 1,
      px: { xs: 1, sm: 3 },
      pb: { xs: 1, sm: 3 },
      maxWidth: 1200,
      mx: 'auto',
      width: '100%',
      minHeight: '100vh',
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: appColors.text.heading, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Incidents
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.5 }}>
            Select an incident to view details or start a new one.
          </Typography>
        </Box>
        {canCreateIncident && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/incidents/new')}
            sx={{ px: 3 }}
          >
            New Incident
          </Button>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Summary Strip + Location Filter */}
          <Box
            data-testid="summary-strip"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 2 },
              mb: 3,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap', mb: { xs: 1, sm: 0 } }}>
              <SummaryItem label="Active" count={active.length} color={appColors.primary.main} bg="rgba(46, 139, 132, 0.06)" border={appColors.primary.main} />
              <SummaryItem label="Counting in Progress" count={summaryCounts.needsAttention} highlight />
              <SummaryItem label="Counting not Started" count={summaryCounts.notStarted} />
              <SummaryItem label="Past" count={past.length} color={appColors.text.caption} />
            </Box>
            {locationOptions.length > 1 && (
              <Box>
                <Typography variant="caption" sx={{ color: appColors.text.caption, mb: 0.25, display: 'block', fontSize: '0.7rem' }}>
                  Location
                </Typography>
                <FormControl size="small" sx={{ minWidth: { xs: 180, sm: 220 } }}>
                  <Select
                    id="location-filter"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    sx={{
                      height: 18,
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': { py: 0, px: '8px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: appColors.border.default },
                    }}
                  >
                    <MenuItem value={ALL_LOCATIONS_VALUE}>All Locations</MenuItem>
                    {locationOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Active Incidents */}
          <Box sx={{
            mb: 5,
            borderLeft: `4px solid ${summaryCounts.needsAttention > 0 ? appColors.status.error : appColors.primary.main}`,
            bgcolor: summaryCounts.needsAttention > 0 ? 'rgba(211, 47, 47, 0.03)' : 'rgba(46, 139, 132, 0.03)',
            borderRadius: { xs: '4px', sm: '4px 12px 12px 4px' },
            p: { xs: 1, sm: 2.5 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: appColors.text.heading }}>
                Active Incidents
              </Typography>
              <Chip
                label={active.length}
                size="small"
                sx={{
                  bgcolor: summaryCounts.needsAttention > 0 ? appColors.status.error : appColors.primary.main,
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 22,
                  minWidth: 22,
                }}
              />
            </Box>
            {active.length === 0 ? (
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" sx={{ color: appColors.text.placeholder }}>
                  {selectedLocation !== ALL_LOCATIONS_VALUE
                    ? `No active incidents for ${locationOptions.find(o => o.value === selectedLocation)?.label ?? 'this location'}.`
                    : 'No active incidents at this time.'}
                </Typography>
                {selectedLocation !== ALL_LOCATIONS_VALUE && (
                  <Button size="small" onClick={() => setSelectedLocation(ALL_LOCATIONS_VALUE)} sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                    Clear location filter
                  </Button>
                )}
              </Box>
            ) : (
              active.map((inc) => <IncidentRow key={inc.incidentId} incident={inc} now={now} onEdit={canEdit ? (i) => navigate(`/incidents/${i.incidentId}/edit`) : undefined} />)
            )}
          </Box>

          {/* Past Incidents */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: appColors.text.heading }}>
                Past Incidents
              </Typography>
              <Chip
                label={past.length}
                size="small"
                sx={{
                  bgcolor: appColors.text.caption,
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 22,
                  minWidth: 22,
                }}
              />
            </Box>
            {past.length === 0 ? (
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" sx={{ color: appColors.text.placeholder }}>
                  {selectedLocation !== ALL_LOCATIONS_VALUE
                    ? `No past incidents for ${locationOptions.find(o => o.value === selectedLocation)?.label ?? 'this location'}.`
                    : 'No past incidents found.'}
                </Typography>
                {selectedLocation !== ALL_LOCATIONS_VALUE && (
                  <Button size="small" onClick={() => setSelectedLocation(ALL_LOCATIONS_VALUE)} sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                    Clear location filter
                  </Button>
                )}
              </Box>
            ) : (
              <>
                {visiblePast.map((inc) => <IncidentRow key={inc.incidentId} incident={inc} compact onEdit={canEdit ? (i) => navigate(`/incidents/${i.incidentId}/edit`) : undefined} />)}
                {hasMorePast && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowAllPast((prev) => !prev)}
                    sx={{ mt: 0.5, fontSize: '0.8125rem', fontWeight: 600 }}
                  >
                    {showAllPast ? 'Show fewer' : `Show all past incidents (${past.length})`}
                  </Button>
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* Footer bar */}
      {!loading && !error && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            pt: 2,
            borderTop: `1px solid ${appColors.border.default}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClockIcon sx={{ fontSize: 18, color: appColors.text.placeholder }} />
            <Typography variant="caption" sx={{ color: appColors.text.caption }}>
              {lastUpdated ? `Last updated: ${lastUpdated}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: appColors.text.disabled, ml: 1 }}>
              Auto-refreshing every 15 seconds
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: appColors.text.caption }}>
            <Typography variant="caption">Need help?</Typography>
            <HelpIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      )}


      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IncidentListPage;
